import React, { useCallback, useMemo, useState } from 'react';
import { createUseStyles } from 'react-jss';
import BottomSheet from './BottomSheet';
import Avatar from './Avatar';
import RobotIcon from '../icons/RobotIcon';
import KickButton from './KickButton';
import { useT } from '../../i18n';

const ROBOT_COLORS = ['#d94f2b', '#2a63a8', '#e0a325', '#2f9e6e'];
const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';

/**
 * Who is here — and, during bidding, what they have bid.
 *
 * `ScoreBoard`'s mobile twin, and it used to be a leaderboard only, which is
 * the one thing you do not need mid-round: the standing bids were desktop-only.
 * Colour is assigned by the player's index in the unsorted `players` array,
 * exactly as `ScoreBoard` does it, so a robot mark does not change hue when
 * the list re-sorts — and so the two panels agree about who is orange.
 *
 * In LOBBY one open row sits under the players, the same single outlined row
 * `ScoreBoard` draws. Deliberately one, and with no number: this game has no
 * player cap, so a set of empty chairs or an `n / 4` count would advertise a
 * limit the server does not enforce.
 *
 * The invite plate at the foot is the phone's second way at the room code —
 * the first, `MobileRoomStrip`, is lobby-only, and a room can want a fifth
 * player mid-game.
 */
const useStyles = createUseStyles({
  content: { padding: '0 12px 20px' },
  head: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '0 4px 10px',
  },
  label: {
    fontFamily: MONO,
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '2px',
    textTransform: 'uppercase',
    color: '#c4a070',
  },
  rule: { flex: 1, height: '1px', background: '#3a2718' },
  count: {
    fontFamily: MONO,
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '1px',
    color: '#a8875d',
  },
  list: { display: 'flex', flexDirection: 'column', gap: '6px' },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '11px',
    minHeight: '56px',
    padding: '0 12px',
    borderRadius: '9px',
    background: '#1c130c',
    boxShadow: 'inset 0 0 0 1px #3a2718, inset 0 2px 4px rgba(0,0,0,0.4)',
  },
  rank: {
    fontFamily: MONO,
    fontSize: '11px',
    fontWeight: 700,
    color: '#a8875d',
    width: '20px',
    flexShrink: 0,
  },
  mark: {
    width: '30px',
    height: '30px',
    flexShrink: 0,
    display: 'grid',
    placeItems: 'center',
    overflow: 'hidden',
    borderRadius: '50%',
    background: '#150d07',
    boxShadow: 'inset 0 0 0 1px #4a3020',
  },
  markInner: { width: '24px', height: '24px' },
  markActive: { boxShadow: 'inset 0 0 0 1px #4a3020, 0 0 0 2px #d94f2b' },
  who: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '1px' },
  name: {
    color: '#f5e6c8',
    fontSize: '14.5px',
    fontWeight: 700,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  tag: { color: '#a8875d', fontSize: '11px', fontWeight: 700 },
  bid: {
    fontFamily: MONO,
    fontSize: '10.5px',
    fontWeight: 700,
    letterSpacing: '1.3px',
    textTransform: 'uppercase',
    color: '#a8875d',
  },
  bidPlaced: { color: '#4fc691' },
  score: {
    fontFamily: MONO,
    fontSize: '19px',
    fontWeight: 700,
    color: '#f5e6c8',
    flexShrink: 0,
  },
  // No plate fill: an open row is an outline, not a player. Same geometry as
  // `row`, so the two line up.
  seat: {
    display: 'flex',
    alignItems: 'center',
    gap: '11px',
    minHeight: '56px',
    padding: '0 12px',
    borderRadius: '9px',
    boxShadow: 'inset 0 0 0 1px #3a2718',
  },
  seatRing: {
    width: '30px',
    height: '30px',
    flexShrink: 0,
    borderRadius: '50%',
    boxShadow: 'inset 0 0 0 1px #4a3020',
  },
  seatLabel: {
    flex: 1,
    fontFamily: MONO,
    fontSize: '11px',
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
    // 3.9:1 on the sheet. Do not darken.
    color: '#8a6e4c',
  },
  invite: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    height: '50px',
    marginTop: '14px',
    background: '#3a2718',
    border: 'none',
    borderRadius: '9px',
    color: '#e8d6ae',
    fontFamily: MONO,
    fontSize: '12px',
    fontWeight: 700,
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    cursor: 'pointer',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), inset 0 -3px 0 rgba(0,0,0,0.4)',
    '&:active': { boxShadow: 'inset 0 2px 5px rgba(0,0,0,0.55)' },
  },
  inviteOk: { color: '#4fc691' },
});

function PlayersSheet({
  open, onClose, players = [], scores = {}, selfId, hostId, onKickPlayer,
  phase, bids, readyToSkip, demoPlayerId, gameId,
}) {
  const classes = useStyles();
  const t = useT();
  const [copied, setCopied] = useState(false);

  const colorOf = useMemo(() => {
    const map = {};
    players.forEach((p, i) => { map[p.id] = ROBOT_COLORS[i % ROBOT_COLORS.length]; });
    return map;
  }, [players]);

  const sorted = useMemo(
    () => [...players].sort((a, b) => (scores[b.id] ?? 0) - (scores[a.id] ?? 0)),
    [players, scores],
  );

  const canKick = Boolean(onKickPlayer) && selfId === hostId;

  const handleInvite = useCallback(async () => {
    const url = `${window.location.origin}/game/${gameId}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: 'Robotrons', url });
        return;
      }
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (e) {
      // A cancelled share, or no clipboard permission — neither is news.
    }
  }, [gameId]);

  // During bidding, what a row says under the name. Nothing in any other
  // phase: a stale bid beside a score is worse than no line at all.
  const bidLine = (player) => {
    if (phase !== 'BIDDING') return null;
    const bid = bids?.find(b => b.playerId === player.id || b.id === player.id);
    if (bid) return { text: t('game.bidBadge', { count: bid.count }), placed: true };
    if (readyToSkip?.includes(player.id)) return { text: t('bidding.readyTag'), placed: false };
    return { text: t('bidding.thinking'), placed: false };
  };

  return (
    <BottomSheet open={open} onClose={onClose}>
      <div className={classes.content}>
        <div className={classes.head}>
          <span className={classes.label}>{t('game.players')}</span>
          <span className={classes.rule} />
          <span className={classes.count}>{String(players.length).padStart(2, '0')}</span>
        </div>

        <div className={classes.list}>
          {sorted.map((player, i) => {
            const line = bidLine(player);
            const color = colorOf[player.id] ?? ROBOT_COLORS[0];
            const isDemo = player.id === demoPlayerId;
            return (
              <div key={player.id} className={classes.row}>
                <span className={classes.rank}>{i + 1}</span>
                <div className={`${classes.mark}${isDemo ? ` ${classes.markActive}` : ''}`}>
                  {player.avatar
                    ? <Avatar photoURL={player.avatar} color={color} size={30} />
                    : <div className={classes.markInner}><RobotIcon color={color} crop="head" /></div>}
                </div>
                <div className={classes.who}>
                  <span className={classes.name}>
                    {player.username ?? t('game.anonymous')}
                    {player.id === selfId && <span className={classes.tag}> · {t('game.you')}</span>}
                    {player.id === hostId && <span className={classes.tag}> · {t('game.host')}</span>}
                  </span>
                  {line && (
                    <span className={`${classes.bid}${line.placed ? ` ${classes.bidPlaced}` : ''}`}>
                      {line.text}
                    </span>
                  )}
                </div>
                <span className={classes.score}>{scores[player.id] ?? 0}</span>
                {canKick && player.id !== selfId && (
                  <KickButton
                    username={player.username}
                    onKick={() => onKickPlayer(player.id)}
                    size={44}
                  />
                )}
              </div>
            );
          })}

          {phase === 'LOBBY' && (
            <div className={classes.seat}>
              <span className={classes.rank} />
              <span className={classes.seatRing} />
              <span className={classes.seatLabel}>{t('game.waitingForPlayers')}</span>
            </div>
          )}
        </div>

        {gameId && (
          <button
            type="button"
            className={`${classes.invite}${copied ? ` ${classes.inviteOk}` : ''}`}
            onClick={handleInvite}
          >
            {copied ? (
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6">
                <path d="M3 8.4l3.2 3.2L13 4.8" />
              </svg>
            ) : (
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
                <rect x="5.4" y="5.4" width="8.2" height="8.2" rx="1.6" />
                <path d="M10.6 5.4V3.9a1.5 1.5 0 0 0-1.5-1.5H3.9a1.5 1.5 0 0 0-1.5 1.5v5.2a1.5 1.5 0 0 0 1.5 1.5h1.5" />
              </svg>
            )}
            {copied ? t('game.linkCopied') : `${t('game.invite')} · ${gameId}`}
          </button>
        )}
      </div>
    </BottomSheet>
  );
}

export default PlayersSheet;
